import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from 'src/hooks/useApiClient';
import type {
	CatalogCandidatesRequest,
	CatalogLinkBody,
	CatalogMatrixAutoRenewBody,
	CatalogMotornetAutoBody,
	CatalogMotornetBody,
	CatalogProviderChangeResponse,
	CatalogRemoveBody,
	CatalogRowFilters,
	CatalogRowsRequest,
	CatalogRowsResponse,
	CatalogUnavailableBody,
	MotornetVehicleKind,
} from 'src/motornet/types';

export const CATALOG_ROWS_QUERY_KEY = 'catalog-versions';
const CATALOG_MAKES_QUERY_KEY = 'catalog-versions-makes';
const CATALOG_MODELS_QUERY_KEY = 'catalog-versions-models';
const CATALOG_CANDIDATES_QUERY_KEY = 'catalog-candidates';
const MOTORNET_MAKES_QUERY_KEY = 'vehicle-association-motornet-makes';
const MOTORNET_MODELS_QUERY_KEY = 'vehicle-association-motornet-models';
const MOTORNET_VERSIONS_QUERY_KEY = 'vehicle-association-motornet-versions';

const FACETS_STALE_TIME = 60_000;

export const PENDING_RETRIES = 3;
export const PENDING_DELAY_MS = 2000;

const queryDefaults = { retry: false, refetchOnWindowFocus: false } as const;

export const useCatalogRows = (request: CatalogRowsRequest) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [CATALOG_ROWS_QUERY_KEY, request],
		queryFn: () => apiClient.motornetControllerService.getCatalogRows(request),
		...queryDefaults,
	});
};

export const useCatalogMakes = (filters: CatalogRowFilters, enabled = true) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [CATALOG_MAKES_QUERY_KEY, filters],
		queryFn: () => apiClient.motornetControllerService.getCatalogMakes(filters),
		placeholderData: keepPreviousData,
		staleTime: FACETS_STALE_TIME,
		enabled,
		...queryDefaults,
	});
};

export const useCatalogModels = (filters: CatalogRowFilters, make: string | undefined, enabled = true) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [CATALOG_MODELS_QUERY_KEY, filters, make],
		queryFn: () => apiClient.motornetControllerService.getCatalogModels(filters, make as string),
		placeholderData: keepPreviousData,
		staleTime: FACETS_STALE_TIME,
		enabled: enabled && !!make,
		...queryDefaults,
	});
};

export const useCatalogCandidates = (request: CatalogCandidatesRequest, enabled = true) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [CATALOG_CANDIDATES_QUERY_KEY, request],
		queryFn: () => apiClient.motornetControllerService.getCatalogCandidates(request),
		enabled,
		placeholderData: keepPreviousData,
		gcTime: 0,
		...queryDefaults,
	});
};

export const useMotornetMakes = (enabled = true) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [MOTORNET_MAKES_QUERY_KEY],
		queryFn: () => apiClient.motornetControllerService.getMakes(),
		enabled,
		staleTime: Infinity,
		...queryDefaults,
	});
};

export const useMotornetModels = (makeCode?: string, vehicleKind?: MotornetVehicleKind) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [MOTORNET_MODELS_QUERY_KEY, makeCode, vehicleKind],
		queryFn: () => apiClient.motornetControllerService.getModels(makeCode as string, { vehicleKind }),
		enabled: !!makeCode,
		staleTime: Infinity,
		...queryDefaults,
	});
};

export const useMotornetVersions = (makeCode?: string, modelCode?: string, vehicleKind?: MotornetVehicleKind) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: [MOTORNET_VERSIONS_QUERY_KEY, makeCode, modelCode, vehicleKind],
		queryFn: () =>
			apiClient.motornetControllerService.getVersions({
				codiceMarca: makeCode as string,
				codiceModello: modelCode as string,
				vehicleKind,
			}),
		enabled: !!makeCode && !!modelCode,
		staleTime: Infinity,
		...queryDefaults,
	});
};

export const retryWhilePending = async (
	call: () => Promise<CatalogProviderChangeResponse>,
	delayMs = PENDING_DELAY_MS
): Promise<CatalogProviderChangeResponse> => {
	let result = await call();
	for (let attempt = 0; attempt < PENDING_RETRIES && result.vehiclesPending; attempt++) {
		await new Promise((resolve) => setTimeout(resolve, delayMs));
		result = await call();
	}
	return result;
};

const useCatalogMutation = <TBody, TResult>(mutationFn: (body: TBody) => Promise<TResult>) => {
	const queryClient = useQueryClient();
	// non attendere il refetch: i callback di mutate devono partire subito
	const invalidate = () => {
		void queryClient.invalidateQueries({ queryKey: [CATALOG_ROWS_QUERY_KEY] });
		void queryClient.invalidateQueries({ queryKey: [CATALOG_MAKES_QUERY_KEY] });
		void queryClient.invalidateQueries({ queryKey: [CATALOG_MODELS_QUERY_KEY] });
	};

	return useMutation({
		mutationFn,
		onSuccess: invalidate,
		onError: (error) => {
			if ((error as Error & { status?: number }).status === 409) invalidate();
		},
	});
};

export const useLinkProvider = () => {
	const { apiClient } = useApiClient();
	return useCatalogMutation((body: CatalogLinkBody) =>
		retryWhilePending(() => apiClient.motornetControllerService.linkCatalogProvider(body))
	);
};

export const useRemoveProvider = () => {
	const { apiClient } = useApiClient();
	return useCatalogMutation((body: CatalogRemoveBody) =>
		retryWhilePending(() => apiClient.motornetControllerService.removeCatalogProvider(body))
	);
};

export const useMarkUnavailable = () => {
	const { apiClient } = useApiClient();
	return useCatalogMutation((body: CatalogUnavailableBody) =>
		apiClient.motornetControllerService.markCatalogProviderUnavailable(body)
	);
};

export const useMarkAvailable = () => {
	const { apiClient } = useApiClient();
	return useCatalogMutation((body: CatalogRemoveBody) =>
		apiClient.motornetControllerService.markCatalogProviderAvailable(body)
	);
};

export const useSetMatrixAutoRenew = () => {
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();
	return useCatalogMutation(async (body: CatalogMatrixAutoRenewBody) => {
		const res = await apiClient.motornetControllerService.setCatalogMatrixAutoRenew(body);
		// aggiorna subito la riga in cache, il refetch dell'invalidate conferma
		queryClient.setQueriesData<CatalogRowsResponse>({ queryKey: [CATALOG_ROWS_QUERY_KEY] }, (data) =>
			data && {
				...data,
				items: data.items.map((r) => (r.versionKey === res.version.versionKey ? res.version : r)),
			}
		);
		return res;
	});
};

export const useSetMotornet = () => {
	const { apiClient } = useApiClient();
	return useCatalogMutation((body: CatalogMotornetBody) =>
		apiClient.motornetControllerService.setCatalogMotornet(body)
	);
};

export const useFindMotornet = () => {
	const { apiClient } = useApiClient();
	return useCatalogMutation((body: CatalogMotornetAutoBody) =>
		apiClient.motornetControllerService.findCatalogMotornet(body)
	);
};
